// @flow strict

import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import AnchorLink from 'react-anchor-link-smooth-scroll'

// Components
import AccessibilityText from '../../../atoms/AccessibilityText'

// Styling
import navigationStyles from './USNavigation.module.scss'
import navigationDropdownStyles from './NavigationDropdown.module.scss'
import navigationListStyles from './NavigationList.module.scss'
import USAButton from '../../../atoms/Button/USAButton'

// Types
type OnClickHandler = (SyntheticMouseEvent<*>) => mixed

type Placement = 'minimal' | 'full'

type ToggleButtonProps = {
  children: React.ChildrenArray<
    string | React.Element<'svg' | typeof AccessibilityText>,
  >,
  onClick: OnClickHandler,
  placement: Placement,
}

type NavigationLink = {
  href?: string,
  text: string,
  label?: string,
}

type DropdownItemProps = NavigationLink & { icon?: string }

type DropdownProps = {
  items: Array<DropdownItemProps>,
  isDropdownVisible: boolean,
  setIsDropdownVisible: (visible: boolean) => void,
}

type NavigationItemProps = NavigationLink & {
  dropdownItems?: Array<DropdownItemProps>,
}

type MinimalNavigationProps = {
  onClick: OnClickHandler,
}

type FullNavigationProps = {
  isNavigationVisible: boolean,
  setIsNavigationVisible: OnClickHandler,
}

type LogoProps = {
  placement: Placement,
}

const NAVIGATION_LINKS: Array<NavigationItemProps> = [
  {
    href: '/usa',
    text: 'Who We Are',
  },
  {
    href: '/blog/usa',
    text: 'Blog',
  },
  {
    href: '/usa/careers',
    text: 'Careers',
  },
  {
    href: 'https://community.monzo.com/c/usa',
    text: 'Community',
  },
  {
    href: '/blog/2021/02/22/monzo-usa-frequently-asked-questions',
    text: 'FAQ',
  },
  {
    href: 'https://app.productstash.io/monzo-usa-public-roadmap',
    text: 'Product Roadmap',
  },
]

const Logo = ({ placement }: LogoProps) => {
  const classes =
    placement === 'minimal'
      ? navigationStyles.logoMinimal
      : navigationStyles.logoFull
  return (
    <a href="/" className={classes}>
      Monzo bank
    </a>
  )
}

const ToggleButton = ({ children, onClick, placement }: ToggleButtonProps) => {
  const classes =
    placement === 'minimal'
      ? navigationStyles.toggleMinimal
      : navigationStyles.toggleFull
  return (
    <button className={classes} onClick={onClick} data-testid="ToggleButton">
      {children}
    </button>
  )
}

const Dropdown = ({
  items,
  isDropdownVisible,
  setIsDropdownVisible,
}: DropdownProps) => {
  const dropdown: {
    current: null | HTMLUListElement,
  } = useRef(null)
  // This allows you to focus into the dropdown and keep the dropdown open
  // And it closes the dropdown, when you focus out of it
  useEffect(() => {
    const currentDropdown = dropdown.current
    if (!currentDropdown) return

    const toggleDropdownVisibility = (
      evt: Event & { relatedTarget?: HTMLElement },
    ) => setIsDropdownVisible(currentDropdown.contains(evt.relatedTarget))
    currentDropdown.addEventListener('focusout', toggleDropdownVisibility)

    return () => {
      currentDropdown.removeEventListener('focusout', toggleDropdownVisibility)
    }
  }, [dropdown, setIsDropdownVisible])
  const listClasses = isDropdownVisible
    ? navigationDropdownStyles.dropdownVisible
    : navigationDropdownStyles.dropdownHidden

  return (
    <ul className={listClasses} ref={dropdown} data-testid="Dropdown">
      {items.map(({ href, text, icon }) => {
        const linkClasses = icon
          ? navigationDropdownStyles.linkWithIcon
          : navigationDropdownStyles.link
        return (
          <li key={text}>
            <a
              href={href}
              className={linkClasses}
              data-testid={icon ? 'DropdownLinkFooter' : 'DropdownLink'}
            >
              {icon && (
                <img
                  src={icon}
                  alt=""
                  className={navigationDropdownStyles.icon}
                />
              )}
              {text}
            </a>
          </li>
        )
      })}
    </ul>
  )
}

const NavigationItem = ({
  text,
  href,
  label,
  dropdownItems,
}: NavigationItemProps) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)

  if (dropdownItems) {
    return (
      <li
        className={navigationListStyles.item}
        onMouseLeave={() => setIsDropdownVisible(false)}
      >
        <button
          className={navigationListStyles.toggle}
          onClick={() => setIsDropdownVisible(!isDropdownVisible)}
          data-testid="DropdownToggle"
        >
          {text}{' '}
          {label && <span className={navigationListStyles.label}>{label}</span>}
          {dropdownItems && dropdownItems.length && (
            <svg>
              <use href="#down-chevron" xlinkHref="#down-chevron" />
            </svg>
          )}
        </button>
        <Dropdown
          isDropdownVisible={isDropdownVisible}
          items={dropdownItems}
          setIsDropdownVisible={setIsDropdownVisible}
        />
      </li>
    )
  }

  return (
    <li className={navigationListStyles.item}>
      <a href={href} className={navigationListStyles.link}>
        {text}{' '}
        {label && <span className={navigationListStyles.label}>{label}</span>}
      </a>
    </li>
  )
}
const NavigationList = () => {
  return (
    <nav className={navigationListStyles.wrapper}>
      <ul className={navigationListStyles.list}>
        {NAVIGATION_LINKS.map(props => (
          <NavigationItem {...props} key={props.text} />
        ))}
      </ul>
    </nav>
  )
}

// Defining SVGs inline as we need to change their fill colours
const SvgDefs = () => (
  <svg className={navigationStyles.icons}>
    <defs>
      <symbol
        id="down-chevron"
        width="12"
        height="7"
        viewBox="0 0 12 7"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M1.138.529a.667.667 0 0 0-.943.942L5.53 6.805c.26.26.682.26.942 0l5.334-5.334a.667.667 0 1 0-.943-.942L6 5.39 1.138.529z" />
      </symbol>
      <symbol
        id="menu-close"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M.293.293a1 1 0 0 1 1.414 0L7 5.585 12.293.293a1 1 0 0 1 1.32-.083l.094.083a1 1 0 0 1 0 1.414L8.415 7l5.292 5.293a1 1 0 0 1 .083 1.32l-.083.094a1 1 0 0 1-1.414 0L7 8.415l-5.293 5.292a1 1 0 0 1-1.32.083l-.094-.083a1 1 0 0 1 0-1.414L5.585 7 .293 1.707A1 1 0 0 1 .21.387z" />
      </symbol>
      <symbol
        id="menu-open"
        width="18"
        height="14"
        viewBox="0 0 18 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g>
          <path d="M16.5 11a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15zm0-5.5a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15zm0-5.5a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3h15z" />
        </g>
      </symbol>
    </defs>
  </svg>
)

const MinimalNavigation = ({ onClick }: MinimalNavigationProps) => (
  <div className={navigationStyles.minimal} data-testid="MinimalNavigation">
    <div className={navigationStyles.container}>
      <div className={navigationStyles.header}>
        <Logo placement="minimal" />
        <ToggleButton onClick={onClick} placement="minimal">
          Menu
          <svg width="18" height="14">
            <use href="#menu-open" xlinkHref="#menu-open" />
          </svg>
        </ToggleButton>
      </div>
    </div>
  </div>
)

const FullNavigation = ({
  isNavigationVisible,
  setIsNavigationVisible,
}: FullNavigationProps) => {
  const classes = isNavigationVisible
    ? navigationStyles.fullVisible
    : navigationStyles.fullHidden

  return (
    <header className={classes} data-testid="FullNavigation">
      <div className={navigationStyles.container}>
        <div className={navigationStyles.header}>
          <Logo placement="full" />
          <ToggleButton onClick={setIsNavigationVisible} placement="full">
            <AccessibilityText>Close Menu</AccessibilityText>
            <svg width="14" height="14">
              <use href="#menu-close" xlinkHref="#menu-close" />
            </svg>
          </ToggleButton>
        </div>
        <NavigationList />
        <div className={navigationStyles.footer}>
          <AnchorLink href="#waitlist-signup">
            <USAButton aria-label="Join the US waiting list">
              Join the waitlist
            </USAButton>
          </AnchorLink>
        </div>
      </div>
    </header>
  )
}

const Overlay = () => <div className={navigationStyles.overlay} />

const USNavigation = () => {
  const [isNavigationVisible, setIsNavigationVisible] = useState(false)
  const toggleNavigationVisibility = () =>
    setIsNavigationVisible(!isNavigationVisible)

  useEffect(() => {
    // Stop the page from been scrollable
    // when the navigation is visible on mobile devices
    const html = document.querySelector('html')
    if (!html) return

    const touchMoveListener = (evt: Event): void => evt.preventDefault()

    if (isNavigationVisible) {
      html.addEventListener('touchmove', touchMoveListener)
      html.classList.add(navigationStyles.noScroll)
    } else {
      html.removeEventListener('touchmove', touchMoveListener)
      html.classList.remove(navigationStyles.noScroll)
    }

    return () => {
      html.removeEventListener('touchmove', touchMoveListener)
    }
  }, [isNavigationVisible])

  return (
    <>
      <SvgDefs />
      <MinimalNavigation onClick={toggleNavigationVisibility} />
      <FullNavigation
        isNavigationVisible={isNavigationVisible}
        setIsNavigationVisible={toggleNavigationVisibility}
      />
      {isNavigationVisible && <Overlay />}
    </>
  )
}

export default USNavigation
